import { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone, CheckCircle2 } from "lucide-react";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

export const InstallPrepIT = () => {
  const [canPrompt, setCanPrompt] = useState(!!(typeof window !== "undefined" && window.__pwaPrompt));
  const [installed, setInstalled] = useState(isStandalone());
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onReady = () => setCanPrompt(true);
    const onInstalled = () => { setInstalled(true); setShowHelp(false); };
    window.addEventListener("pwaPromptReady", onReady);
    window.addEventListener("pwaInstalled", onInstalled);
    return () => {
      window.removeEventListener("pwaPromptReady", onReady);
      window.removeEventListener("pwaInstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const dp = typeof window !== "undefined" ? window.__pwaPrompt : null;
    if (dp) {
      dp.prompt();
      try {
        const { outcome } = await dp.userChoice;
        if (outcome === "accepted") { window.__pwaPrompt = null; setCanPrompt(false); }
      } catch (e) { /* noop */ }
      return;
    }
    setShowHelp(true); // iOS / browsers without the native prompt
  };

  const ios = isIOS();

  return (
    <>
      <div className="mt-5 flex flex-col items-center gap-2 gdm-rise" style={{ animationDelay: "0.3s" }}>
        <button
          type="button"
          data-testid="install-prepit-btn"
          onClick={handleClick}
          className="group flex items-center gap-3 rounded-xl border border-[#38bdf8]/30 bg-[#38bdf8]/10 hover:bg-[#38bdf8]/20 px-4 py-2.5 transition-colors"
        >
          <img src="/prepit/icon-192.png" alt="PrepIT" className="h-9 w-9 rounded-lg shadow-[0_0_18px_rgba(56,189,248,0.35)]" />
          <span className="text-left leading-tight">
            <span className="block text-[13px] font-semibold text-white flex items-center gap-1.5">
              Get the PrepIT app <Download className="h-3.5 w-3.5 text-[#38bdf8]" />
            </span>
            <span className="block text-[11px] text-white/50">Install to your phone · iPhone &amp; Android</span>
          </span>
        </button>
      </div>

      {showHelp && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="install-prepit-modal"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0d1522] p-5 shadow-2xl gdm-rise"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <img src="/prepit/icon-192.png" alt="PrepIT" className="h-11 w-11 rounded-xl" />
                <div>
                  <div className="text-sm font-semibold text-white">Install PrepIT</div>
                  <div className="text-[11px] text-white/50">Add it to your home screen</div>
                </div>
              </div>
              <button data-testid="install-prepit-close" onClick={() => setShowHelp(false)} className="text-white/50 hover:text-white p-1" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {ios ? (
              <ol className="space-y-3 text-sm text-white/80">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[#38bdf8]/15 text-[#38bdf8] text-xs font-bold">1</span>
                  <span className="flex items-center gap-1.5 flex-wrap">Tap the <strong>Share</strong> button <Share className="h-4 w-4 text-[#38bdf8]" /> in Safari's toolbar.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[#38bdf8]/15 text-[#38bdf8] text-xs font-bold">2</span>
                  <span className="flex items-center gap-1.5 flex-wrap">Scroll and tap <strong>Add to Home Screen</strong> <Plus className="h-4 w-4 text-[#38bdf8]" />.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[#38bdf8]/15 text-[#38bdf8] text-xs font-bold">3</span>
                  <span className="flex items-center gap-1.5">Tap <strong>Add</strong> — PrepIT lands on your home screen. <CheckCircle2 className="h-4 w-4 text-emerald-400" /></span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-white/80">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[#38bdf8]/15 text-[#38bdf8] text-xs font-bold">1</span>
                  <span className="flex items-center gap-1.5">Open this page in <strong>Chrome</strong> on your phone <Smartphone className="h-4 w-4 text-[#38bdf8]" />.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[#38bdf8]/15 text-[#38bdf8] text-xs font-bold">2</span>
                  <span>Open the <strong>⋮ menu</strong> and choose <strong>Install app</strong> / <strong>Add to Home screen</strong>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-[#38bdf8]/15 text-[#38bdf8] text-xs font-bold">3</span>
                  <span className="flex items-center gap-1.5">Confirm — PrepIT installs like a native app. <CheckCircle2 className="h-4 w-4 text-emerald-400" /></span>
                </li>
              </ol>
            )}

            <button
              onClick={() => setShowHelp(false)}
              className="mt-5 w-full h-10 rounded-lg bg-[#38bdf8] text-[#06121b] text-sm font-semibold hover:bg-[#5cc9fb] transition-colors"
              data-testid="install-prepit-gotit"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};
